import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Search, X, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { haversineKm } from '@/lib/geo';
import { formatPetDate } from '@/utils/formatDate';
import PetCard from '@/components/PetCard';
import Pagination from '@/components/Pagination';

const CORDOBA_CENTER = [-31.4201, -64.1888];
const MAP_PAGE_SIZE = 18;

const buildPinIcon = (fill, stroke) =>
  new L.Icon({
    iconUrl:
      'data:image/svg+xml;base64,' +
      btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="10" fill="${fill}" stroke="${stroke}" stroke-width="2"/>
          <circle cx="16" cy="16" r="4" fill="white"/>
        </svg>
      `),
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -14],
  });

const searchPointIcon = buildPinIcon('#1F2937', '#111827');
const lostIcon = buildPinIcon('#FF8C66', '#FF7A50');
const foundIcon = buildPinIcon('#6FCF97', '#4FB37A');

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

const MapSearchView = ({ pets, petTypeFilter, statusFilter, onSelectPet }) => {
  const [searchPoint, setSearchPoint] = useState(null);
  const [radiusKm, setRadiusKm] = useState(3);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [mapPage, setMapPage] = useState(0);

  const handlePick = (point) => {
    setSearchPoint(point);
    setSearchTriggered(false);
  };

  const handleRadiusChange = (val) => {
    setRadiusKm(val[0]);
    setSearchTriggered(false);
  };

  const handleClear = () => {
    setSearchPoint(null);
    setSearchTriggered(false);
  };

  const filteredPets = useMemo(() => {
    if (!searchTriggered || !searchPoint) return [];
    const [lat, lng] = searchPoint;
    return pets.filter((pet) => {
      if (pet.latitude == null || pet.longitude == null) return false;
      if (petTypeFilter && pet.pet_type !== petTypeFilter) return false;
      if (statusFilter !== 'all' && pet.type !== statusFilter) return false;
      return haversineKm(lat, lng, pet.latitude, pet.longitude) <= radiusKm;
    });
  }, [pets, searchPoint, radiusKm, searchTriggered, petTypeFilter, statusFilter]);

  // Reset to the first page whenever the filtered set changes (re-search,
  // radius change, clear) so we never land on a now-empty page.
  useEffect(() => {
    setMapPage(0);
  }, [filteredPets]);

  const mapMaxPage = Math.max(0, Math.ceil(filteredPets.length / MAP_PAGE_SIZE) - 1);
  const pagedPets = filteredPets.slice(mapPage * MAP_PAGE_SIZE, mapPage * MAP_PAGE_SIZE + MAP_PAGE_SIZE);

  const handleSearch = () => {
    if (!searchPoint) return;
    setSearchTriggered(true);
    const [lat, lng] = searchPoint;
    const matches = pets.filter((pet) => {
      if (pet.latitude == null || pet.longitude == null) return false;
      if (petTypeFilter && pet.pet_type !== petTypeFilter) return false;
      if (statusFilter !== 'all' && pet.type !== statusFilter) return false;
      return haversineKm(lat, lng, pet.latitude, pet.longitude) <= radiusKm;
    });
    if (matches.length === 0) {
      toast.info('No se encontraron mascotas en esta zona');
    } else {
      toast.success(`${matches.length} mascota${matches.length === 1 ? '' : 's'} en la zona`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-3xl overflow-hidden border border-border shadow-sm">
        <MapContainer
          center={CORDOBA_CENTER}
          zoom={12}
          scrollWheelZoom={true}
          className="h-[500px] md:h-[600px] w-full"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />

          <ClickHandler onPick={handlePick} />

          {searchPoint && (
            <>
              <Marker position={searchPoint} icon={searchPointIcon} />
              <Circle
                center={searchPoint}
                radius={radiusKm * 1000}
                pathOptions={{ color: '#FF8C66', fillColor: '#FF8C66', fillOpacity: 0.12, weight: 2 }}
              />
            </>
          )}

          {searchTriggered &&
            filteredPets.map((pet) => (
              <Marker
                key={pet.pet_id}
                position={[pet.latitude, pet.longitude]}
                icon={pet.type === 'lost' ? lostIcon : foundIcon}
              >
                <Popup>
                  <div className="w-56">
                    <div className="h-32 bg-muted rounded-lg overflow-hidden mb-2">
                      {pet.photos?.[0] ? (
                        <img
                          src={pet.photos[0]}
                          alt={pet.name || 'Mascota'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl">🐾</div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-fraunces font-semibold text-base">
                        {pet.name || 'Sin nombre'}
                      </span>
                      <Badge
                        variant={pet.type === 'lost' ? 'destructive' : 'default'}
                        className="rounded-full text-[10px]"
                      >
                        {pet.type === 'lost' ? 'Perdida' : 'Encontrada'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground m-0">
                      {pet.pet_type === 'dog' ? '🐕 Perro' : '🐈 Gato'}
                      {pet.date_lost_found && (
                        <> · {formatPetDate(pet.date_lost_found)}</>
                      )}
                    </p>
                    <Button
                      size="sm"
                      onClick={() => onSelectPet(pet.pet_id)}
                      className="w-full mt-2 rounded-full"
                    >
                      Ver perfil
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
        </MapContainer>
      </div>

      <div className="rounded-3xl border border-border bg-white/80 backdrop-blur-sm p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 text-primary" />
          {searchPoint ? (
            <span>
              Zona seleccionada:{' '}
              <span className="text-foreground font-medium">
                {searchPoint[0].toFixed(4)}, {searchPoint[1].toFixed(4)}
              </span>
            </span>
          ) : (
            <span>Hace click en el mapa sobre el barrio o zona donde buscar.</span>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="font-medium">Radio de búsqueda</span>
            <span className="text-muted-foreground">{radiusKm} km</span>
          </div>
          <Slider
            value={[radiusKm]}
            min={1}
            max={10}
            step={1}
            onValueChange={handleRadiusChange}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleSearch}
            disabled={!searchPoint}
            className="rounded-full flex-1 min-w-0"
          >
            <Search className="h-4 w-4 mr-2" />
            Buscar mascota
          </Button>
          {(searchPoint || searchTriggered) && (
            <Button variant="outline" onClick={handleClear} className="rounded-full">
              <X className="h-4 w-4 mr-2" />
              Limpiar
            </Button>
          )}
        </div>

        {searchTriggered && (
          <p className="text-xs text-muted-foreground">
            {filteredPets.length} resultado{filteredPets.length === 1 ? '' : 's'} en {radiusKm} km
          </p>
        )}
      </div>

      {searchTriggered && (
        filteredPets.length > 0 ? (
          <div className="space-y-4">
            <h3 className="font-fraunces font-semibold text-lg">
              {filteredPets.length} mascota{filteredPets.length === 1 ? '' : 's'} en {radiusKm} km
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {pagedPets.map((pet) => (
                <PetCard key={pet.pet_id} pet={pet} onClick={() => onSelectPet(pet.pet_id)} />
              ))}
            </div>

            {/* Paginación */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 pt-2">
              <p className="text-sm text-muted-foreground">
                {filteredPets.length} mascota(s) — Página {mapPage + 1} de {mapMaxPage + 1}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={mapPage === 0}
                  onClick={() => setMapPage(p => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Pagination page={mapPage} totalPages={mapMaxPage + 1} onPageChange={setMapPage} />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={mapPage >= mapMaxPage}
                  onClick={() => setMapPage(p => p + 1)}
                >
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-3xl border border-border bg-white/80 backdrop-blur-sm py-12 text-center">
            <p className="text-4xl mb-4">🔍</p>
            <p className="text-muted-foreground">No se encontraron mascotas en esta zona</p>
          </div>
        )
      )}
    </div>
  );
};

export default MapSearchView;
